import { Card, DatePicker, Divider, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import TransactionIcon from "../../../assets/icons/TransactionIcon";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import {
  useGetAllCollectionPointQuery,
  useGetTransactionsQuery,
  useGetTransitionDetailsQuery,
} from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import useError from "../../../hooks/useError";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import {
  GetAllTransactionsDataParamsType,
  GetAllTransactionsDataType,
} from "../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";

export default function TransactionDetails() {
  const backLink = "/admin/cash-transactions";
  const { RangePicker } = DatePicker;
  const { id, srId } = useParams();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllTransactionsDataParamsType>();

  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);

  const { user } = useAppSelector((state) => state.auth);

  const { isError, error, isLoading, data } = useGetTransitionDetailsQuery({
    id,
    srId,
    dateFrom: dateRange[0],
    dateTo: dateRange[1],
  });

  const {
    isError: getTransactionsIsError,
    error: getTransactionsError,
    isLoading: getTransactionsIsLoading,
    data: getTransactionsData,
  } = useGetTransactionsQuery({
    params: {
      ...filter,
      skip: undefined,
      page,
      limit,
      companyId: user?.data?.company_id,
    },
  });

  const {
    isError: getAllCollectionPointIsError,
    error: getAllCollectionPointError,
    isLoading: getAllCollectionPointIsLoading,
    data: getAllCollectionPointData,
  } = useGetAllCollectionPointQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  useError(isError, error, "Get transaction info data fail");
  useError(
    getTransactionsIsError,
    getTransactionsError,
    "Get transaction data fail",
  );
  useError(
    getTransactionsIsError,
    getTransactionsError,
    "Get transaction data fail",
  );
  useError(
    getAllCollectionPointIsError,
    getAllCollectionPointError,
    "Get collection point data fail",
  );

  const infoData = data?.data;

  // Left Section Data
  const leftData = [
    {
      label: "Route",
      value: infoData?.Route?.routeName
        ? capitalize(infoData?.Route?.routeName)
        : "N/A",
    },
    {
      label: "Field Officer Name",
      value: infoData?.createdByUser?.username
        ? capitalize(infoData?.createdByUser?.username)
        : "N/A",
    },
    {
      label: "Bank Account Number",
      value: infoData?.Route?.bank?.accountNumber
        ? capitalize(infoData?.Route?.bank?.accountNumber)
        : "N/A",
    },
  ];

  const total =
    Number(infoData?.totalAmount || 0) - Number(infoData?.totalDeposit || 0);

  // Right Section Data
  const rightData = [
    {
      label: "Total Collection",
      value: infoData?.totalRemaining
        ? `৳ ${formatNumber(infoData?.totalAmount)}`
        : "৳ 0",
    },
    {
      label: "Total Deposit",
      value: infoData?.totalDeposit
        ? `৳ ${formatNumber(infoData?.totalDeposit)}`
        : "৳ 0",
    },
    {
      label: "Remaining",
      value: total ? `৳ ${formatNumber(total)}` : "৳ 0",
    },
  ];

  const columns: ColumnsType<GetAllTransactionsDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap">Date</div>,
      dataIndex: "createdAt",
      align: "center",
      render: (val) => {
        return val ? formatDate(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Collection Point</div>,
      dataIndex: "collectionPoint",
      align: "center",
      render: (val) => {
        return val?.organizationName
          ? capitalize(val?.organizationName)
          : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Address</div>,
      dataIndex: "collectionPoint",
      align: "center",
      render: (val) => {
        return val?.address ? capitalize(val?.address) : "N/A";
      },
    },
    {
      title: <div className="whitespace-nowrap">Collection</div>,
      dataIndex: "amount",
      align: "center",
      render: (val) => {
        const amount = Number(val) || 0;
        return `৳ ${formatNumber(amount)}`;
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="Transaction Details" backLink={backLink}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <Card className="mb-5">
              <div className="flex justify-between items-center">
                <div className="flex justify-start items-center gap-2">
                  <div>
                    <TransactionIcon />
                  </div>
                  <div className="justify-start text-zinc-700 text-xl font-medium">
                    Transaction Info
                  </div>
                </div>

                <div>
                  <RangePicker
                    allowClear
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    onChange={(dates, dateStrings) => {
                      if (dates?.[0] && dates?.[1]) {
                        setDateRange([
                          dateStrings[0], // start date
                          dates[1].add(1, "day").format("YYYY-MM-DD"), // ✅ safely add +1 day
                        ]);
                      } else {
                        setDateRange([undefined, undefined]);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex w-full mt-5">
                {/* Left Section */}
                <div className="flex-1 pr-4">
                  {leftData.map((item, index) => (
                    <div
                      key={index}
                      className={`flex justify-between py-2 ${
                        index !== leftData.length - 1
                          ? "border-b border-[#D7DFE9]"
                          : ""
                      }`}
                    >
                      <span className="text-slate-500 text-base font-normal">
                        {item.label}
                      </span>
                      <span className="text-zinc-700 text-base font-normal">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Middle Divider */}
                <Divider
                  type="vertical"
                  className="bg-[#D7DFE9]"
                  style={{ height: "auto" }}
                />

                {/* Right Section */}
                <div className="flex-1 pl-4">
                  {rightData.map((item, index) => (
                    <div
                      key={index}
                      className={`flex justify-between py-2 ${
                        index !== rightData.length - 1
                          ? "border-b border-[#D7DFE9]"
                          : ""
                      }`}
                    >
                      <span className="text-slate-500  text-base font-normal">
                        {item.label}
                      </span>
                      <span className="text-zinc-700 text-base font-normal">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <CustomTable
              loading={getTransactionsIsLoading}
              limit={limit}
              page={page}
              columns={columns}
              total={getTransactionsData?.data?.count}
              dataSource={addKeyInArray(getTransactionsData?.data?.rows)}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              filterSection={
                <>
                  <Select
                    placeholder="Select Collection Point"
                    suffixIcon={<SelectDownIcon />}
                    showSearch
                    allowClear
                    style={{ minWidth: 210, width: "100%" }}
                    options={
                      getAllCollectionPointData?.data?.rows?.map(
                        (item: any) => ({
                          label: capitalize(item?.organizationName),
                          value: item?.id,
                        }),
                      ) || []
                    }
                    value={filter?.collectionPointId ?? undefined}
                    onChange={(value) =>
                      handleFilter("collectionPointId", value)
                    }
                    optionFilterProp="label"
                  />

                  <RangePicker
                    allowClear
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        handleFilter("dateFrom", dates[0].format("YYYY-MM-DD"));
                        handleFilter(
                          "dateTo",
                          dates[1].add(1, "day").format("YYYY-MM-DD"),
                        );
                      } else {
                        // reset if cleared
                        handleFilter("dateFrom", undefined);
                        handleFilter("dateTo", undefined);
                      }
                    }}
                  />
                </>
              }
            />
          </>
        )}
      </SidebarLayout>
    </>
  );
}
